// Copyright (c) 2026 Solartek LLC. All rights reserved.
import { z } from 'zod';
import { CONNECTION_STATUSES, DEVICE_STATUSES, INTEGRATION_TYPES, PROVIDER_STATUSES, PUNCH_DIRECTIONS, RAW_SOURCES, VERIFICATION_METHODS, VERIFICATION_STATUSES } from './enums.js';
import { codeSchema, isoDateTimeSchema, jsonObjectSchema, timezoneSchema, uuidSchema } from './common.js';
/** Capability matrix exposed by providers/models/devices (§12). Unknown keys default to false. */
export const deviceCapabilitiesSchema = z.object({
    attendancePull: z.boolean().default(false),
    attendancePush: z.boolean().default(false),
    employeePush: z.boolean().default(false),
    employeePull: z.boolean().default(false),
    employeeDelete: z.boolean().default(false),
    fingerprint: z.boolean().default(false),
    face: z.boolean().default(false),
    card: z.boolean().default(false),
    pin: z.boolean().default(false),
    deviceStatus: z.boolean().default(false),
    remoteRestart: z.boolean().default(false),
    webhooks: z.boolean().default(false),
    devicePush: z.boolean().default(false),
    biometricTemplatePush: z.boolean().default(false),
});
export const CAPABILITY_KEYS = Object.keys(deviceCapabilitiesSchema.shape);
/** Declarative description of the fields a provider needs at registration; drives the wizard. */
export const providerConfigFieldSchema = z.object({
    key: z.string().regex(/^[a-zA-Z][a-zA-Z0-9_]{0,63}$/),
    label: z.string(),
    type: z.enum(['text', 'password', 'number', 'url', 'select', 'boolean']),
    required: z.boolean().default(false),
    secret: z.boolean().default(false),
    options: z.array(z.string()).optional(),
    default: z.union([z.string(), z.number(), z.boolean()]).optional(),
    help: z.string().optional(),
});
export const providerConfigSchemaSchema = z.object({ fields: z.array(providerConfigFieldSchema) });
export const providerThrottlingSchema = z.object({
    maxConcurrentPerDevice: z.number().int().min(1).default(1),
    maxConcurrentPerAccount: z.number().int().min(1).default(4),
    requestsPerMinute: z.number().int().min(1).default(120),
}).partial();
export const deviceProviderDtoSchema = z.object({
    key: z.string(),
    vendor: z.string(),
    name: z.string(),
    description: z.string().nullable(),
    integrationType: z.enum(INTEGRATION_TYPES),
    status: z.enum(PROVIDER_STATUSES),
    capabilities: deviceCapabilitiesSchema,
    configSchema: providerConfigSchemaSchema,
    verificationStatus: z.enum(VERIFICATION_STATUSES),
    docsUrl: z.string().nullable(),
});
export const deviceModelDtoSchema = z.object({
    id: uuidSchema,
    providerKey: z.string(),
    vendor: z.string(),
    model: z.string(),
    family: z.string().nullable(),
    capabilities: deviceCapabilitiesSchema.partial(),
    verification: z.enum(VERIFICATION_STATUSES),
    notes: z.string().nullable(),
});
/**
 * A device's provider configuration (non-secret + secret values keyed by the provider's config field). Bounded like every
 * other request field (security gate, Prompt 10): at most 64 fields, keys up to 64 characters, text values up to 4 KB.
 */
export const DEVICE_CONFIG_MAX_FIELDS = 64;
const deviceConfigSchema = z.record(z.string().min(1).max(64), z.union([z.string().max(4096), z.number(), z.boolean()]))
    .refine((v) => Object.keys(v).length <= DEVICE_CONFIG_MAX_FIELDS, { message: `At most ${DEVICE_CONFIG_MAX_FIELDS} configuration fields` });
export const createDeviceSchema = z.object({
    code: codeSchema,
    name: z.string().trim().min(1).max(120),
    branchId: uuidSchema,
    /** Where the terminal is installed: a place (site, floor, zone…) of its branch (docs/locations.md); null clears it. */
    locationId: uuidSchema.nullable().optional(),
    providerKey: z.string().min(1).max(64),
    modelId: uuidSchema.optional(),
    manufacturer: z.string().trim().min(1).max(80),
    modelName: z.string().trim().max(120).optional(),
    serialNumber: z.string().trim().max(120).optional(),
    timezone: timezoneSchema.optional(),
    endpointUrl: z.url().max(500).optional(),
    /** Non-secret + secret config values keyed by provider config field. Secrets are split out server-side. */
    config: deviceConfigSchema.default({}),
    offlineThresholdMinutes: z.number().int().min(1).max(1440).optional(),
    autoSyncEnabled: z.boolean().optional(),
    syncIntervalMinutes: z.number().int().min(1).max(1440).optional(),
    tags: z.array(z.string().trim().min(1).max(40)).max(20).optional(),
    notes: z.string().max(2000).optional(),
});
export const updateDeviceSchema = createDeviceSchema.omit({ providerKey: true, config: true }).partial().extend({ status: z.enum(DEVICE_STATUSES).optional() });
export const testConnectionSchema = z.object({
    providerKey: z.string().min(1).max(64),
    config: deviceConfigSchema.default({}),
    /** When testing an existing device without re-entering secrets. */
    deviceId: uuidSchema.optional(),
});
export const deviceDtoSchema = z.object({
    id: uuidSchema,
    organizationId: uuidSchema,
    branchId: uuidSchema,
    branchName: z.string().optional(),
    /** The place the terminal is installed in (null = the branch, no finer place). */
    locationId: uuidSchema.nullable().optional(),
    /** That place's path below the branch, e.g. "Site A › Floor 2 › Zone C". */
    locationName: z.string().nullable().optional(),
    code: z.string(),
    name: z.string(),
    providerKey: z.string(),
    providerName: z.string().optional(),
    modelId: uuidSchema.nullable(),
    manufacturer: z.string(),
    modelName: z.string().nullable(),
    serialNumber: z.string().nullable(),
    timezone: z.string(),
    integrationType: z.enum(INTEGRATION_TYPES),
    endpointUrl: z.string().nullable(),
    config: jsonObjectSchema,
    capabilities: deviceCapabilitiesSchema,
    status: z.enum(DEVICE_STATUSES),
    connectionStatus: z.enum(CONNECTION_STATUSES),
    lastHeartbeatAt: isoDateTimeSchema.nullable(),
    lastAttendanceSyncAt: isoDateTimeSchema.nullable(),
    lastEmployeeSyncAt: isoDateTimeSchema.nullable(),
    lastSuccessfulCommunicationAt: isoDateTimeSchema.nullable(),
    lastErrorCode: z.string().nullable(),
    lastError: z.string().nullable(),
    firmwareVersion: z.string().nullable(),
    offlineThresholdMinutes: z.number().int(),
    autoSyncEnabled: z.boolean(),
    syncIntervalMinutes: z.number().int(),
    employeeCount: z.number().int().optional(),
    tags: z.array(z.string()),
    maskedCredentials: jsonObjectSchema.optional(),
    createdAt: isoDateTimeSchema,
    updatedAt: isoDateTimeSchema,
});
/** Vendor-neutral raw transaction as produced by any provider (§21). */
export const rawTransactionSchema = z.object({
    providerTransactionId: z.string().max(200).nullable(),
    deviceEmployeeId: z.string().min(1).max(64),
    punchedAt: isoDateTimeSchema,
    deviceLocalTime: z.string().max(64).nullable().optional(),
    verificationMethod: z.enum(VERIFICATION_METHODS).default('unknown'),
    direction: z.enum(PUNCH_DIRECTIONS).default('unknown'),
    rawPayload: jsonObjectSchema.default({}),
});
export const ingestRawBatchSchema = z.object({
    source: z.enum(RAW_SOURCES),
    syncJobId: uuidSchema.nullable().optional(),
    transactions: z.array(rawTransactionSchema).max(5000),
});
/** Employee representation understood by devices (§34). No biometric templates by default. */
export const deviceEmployeeSchema = z.object({
    deviceUserId: z.string().min(1).max(64),
    name: z.string().min(1).max(64),
    cardNumber: z.string().max(64).nullable().optional(),
    pin: z.string().max(16).nullable().optional(),
    privilege: z.enum(['user', 'admin']).default('user'),
    enabled: z.boolean().default(true),
    photoUrl: z.url().nullable().optional(),
    /** Opaque vendor fields (never templates). */
    extra: jsonObjectSchema.default({}),
});
//# sourceMappingURL=devices.js.map